import * as Sentry from "@sentry/vue";
import {liveServiceApi} from "@/container";
import {supportsLiveServiceUpdate} from "@/core/services/shellCapabilities";
import {useSystemStore} from "@/stores/system.store";
import {LiveServiceUpdateResult} from "@/live/interfaces";

/**
 * Connect stays open for days at the counter: the offer is read again on this
 * interval, as the shell update's, so a version opened on the API reaches the
 * workstation without a restart.
 */
const OFFER_REFRESH_MS = 30 * 60 * 1000;

/** At most one update asked per hour: the service refuses more anyway. */
const UPDATE_SPACING_MS = 60 * 60 * 1000;

/** When this workstation last asked for an update, kept across reloads. */
const ASKED_AT_KEY = 'liveServiceUpdateAskedAt';

/** Whether version `a` (x.y.z) is newer than `b`. */
function isNewer(a: string, b: string): boolean {
    const left = a.split('.').map(Number);
    const right = b.split('.').map(Number);

    for (let i = 0; i < Math.max(left.length, right.length); i++) {
        const difference = (left[i] || 0) - (right[i] || 0);

        if (difference !== 0) {
            return difference > 0;
        }
    }

    return false;
}

function askedRecently(): boolean {
    try {
        return Date.now() - Number(localStorage.getItem(ASKED_AT_KEY) ?? 0) < UPDATE_SPACING_MS;
    } catch {
        return false;
    }
}

function recordAsked(): void {
    try {
        localStorage.setItem(ASKED_AT_KEY, String(Date.now()));
    } catch {
        // The service's own hourly limit still holds.
    }
}

/**
 * LIVE — keeps the Apodis Live service of this workstation at the version the
 * API offers (AP-7768), without UAC nor any maintenance tool: the service runs
 * as SYSTEM and updates itself on request.
 *
 * At start, then each time the offer is read again, the service is asked to
 * update when the offered version is newer than its own and it announces the
 * `update` feature. The detection pauses for a few seconds during the swap.
 * Once the service runs a new version, the host registration reports it.
 *
 * Does nothing on a shell without the capability. Returns how to stop.
 */
export function startLiveServiceUpdates(): () => void {
    const {getLivePatientWatchStatus, updateLiveService} = window.ipcRenderer;

    if (!supportsLiveServiceUpdate() || !updateLiveService || !getLivePatientWatchStatus) {
        return () => {};
    }

    let reportedVersion: string | null | undefined;

    const check = async () => {
        const status = await getLivePatientWatchStatus().catch(() => null);
        const serviceVersion = status?.serviceVersion;

        // The service now runs another version (an update went through): the
        // host registration reports it.
        if (reportedVersion !== undefined && serviceVersion !== undefined && serviceVersion !== reportedVersion) {
            void useSystemStore().updateHostName();
        }

        if (serviceVersion !== undefined) {
            reportedVersion = serviceVersion;
        }

        const offer = await liveServiceApi().offer().catch(() => null);

        if (
            !offer?.target_version
            || !serviceVersion
            || !status?.serviceFeatures?.includes('update')
            || !isNewer(offer.target_version, serviceVersion)
            || askedRecently()
        ) {
            return;
        }

        recordAsked();

        const result: LiveServiceUpdateResult = await updateLiveService(offer.target_version, offer.sha256 ?? '')
            .catch((error: unknown) => ({status: 'failed', reason: String(error)}));

        if (result.status === 'failed') {
            Sentry.captureMessage('Apodis Live service update failed', {
                level: 'warning',
                extra: {from: serviceVersion, to: offer.target_version, reason: result.reason},
            });
        }
    };

    void check();

    const timer = setInterval(check, OFFER_REFRESH_MS);

    return () => clearInterval(timer);
}
